import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

interface WorkflowStepContextValue {
    activeStepId: string;
    setActiveStepId: (stepId: string) => void;
}

// ============================================================================
// Context
// ============================================================================

const WorkflowStepContext = createContext<WorkflowStepContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

export interface WorkflowStepProviderProps {
    defaultStepId: string;
    /** Set of valid step IDs from the workflow config — used to reject invalid transitions. */
    validStepIds: ReadonlySet<string>;
    children: ReactNode;
}

export function WorkflowStepProvider({ defaultStepId, validStepIds, children }: WorkflowStepProviderProps) {
    const [activeStepId, setActiveStepIdRaw] = useState(defaultStepId);

    const setActiveStepId = useCallback(
        (stepId: string) => {
            if (!validStepIds.has(stepId)) {
                console.warn(
                    `[WorkflowStepProvider] setActiveStepId called with unknown step id: "${stepId}"`
                );
                return;
            }
            setActiveStepIdRaw(stepId);
        },
        [validStepIds]
    );

    const value = useMemo(() => ({ activeStepId, setActiveStepId }), [activeStepId, setActiveStepId]);

    return <WorkflowStepContext.Provider value={value}>{children}</WorkflowStepContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useWorkflowStep(): WorkflowStepContextValue {
    const ctx = useContext(WorkflowStepContext);
    if (!ctx) {
        throw new Error('useWorkflowStep must be used inside WorkflowStepProvider');
    }
    return ctx;
}
